import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  NgZone,
  reflectComponentType,
  Type,
} from '@angular/core';
import {createContext, useContext, useLayoutEffect, useRef} from 'react';

export interface AngularContextModel {
  injector: EnvironmentInjector;
  appRef: ApplicationRef;
  ngZone: NgZone;
}

export const AngularContext = createContext<AngularContextModel | null>(null);

export function useAngularContext(): AngularContextModel {
  const context = useContext(AngularContext);
  if (context === null) {
    throw new Error('AngularContext is missing, the component has to be rendered below mountReactApp()');
  }
  return context;
}

interface Props<C> {
  component: Type<C>;
  inputs?: Partial<Record<keyof C & string, unknown>>;
}

function hostElementName(component: Type<unknown>): string {
  const selector = reflectComponentType(component)?.selector ?? '';
  const name = selector.split(',')[0].trim();
  return /^[a-z][a-z0-9-]*$/i.test(name) ? name : 'div';
}

export function AngularHost<C>({component, inputs}: Props<C>) {
  const {injector, appRef, ngZone} = useAngularContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const componentRef = useRef<ComponentRef<C> | null>(null);

  useLayoutEffect(() => {
    const hostElement = document.createElement(hostElementName(component));
    containerRef.current.appendChild(hostElement);

    const ref = ngZone.run(() => {
      const created = createComponent(component, {environmentInjector: injector, hostElement});
      appRef.attachView(created.hostView);
      return created;
    });
    componentRef.current = ref;

    return () => {
      componentRef.current = null;
      ngZone.run(() => {
        appRef.detachView(ref.hostView);
        ref.destroy();
      });
      hostElement.remove();
    };
  }, [component, injector, appRef, ngZone]);

  useLayoutEffect(() => {
    const ref = componentRef.current;
    if (ref === null) {
      return;
    }
    ngZone.run(() => {
      Object.entries(inputs ?? {}).forEach(([name, value]) => ref.setInput(name, value));
      ref.changeDetectorRef.detectChanges();
    });
  }, [component, inputs, ngZone]);

  return <div ref={containerRef} style={{display: 'contents'}}/>;
}
